import { Controller } from "@hotwired/stimulus";

// Copies a value to the clipboard and confirms it on the button itself.
//
// Four shapes, depending on what the button holds:
//
// Label swap — the button's text is replaced, then restored:
//   <div data-controller="clipboard" data-clipboard-text-value="https://…">
//     <button data-action="clipboard#copy">
//       <span data-clipboard-target="label" data-copied="Copied">Copy link</span>
//     </button>
//   </div>
//
// Label swap on an icon-only button — there is no text to replace, so the
// confirmation goes into the tooltip instead. Carrying `data-tip` is what
// selects this; read/write below is the only place the difference lives.
//   <button data-clipboard-target="label" data-tip="Copy link" data-copied="Copied">
//
// Label swap with an icon — two whole labels trade places, the same way
// send_test_controller reports a send. Pair the copied label with a
// `.draw-check` circle-check and the tick draws itself: the animation runs when
// the element becomes rendered, so revealing it is enough to replay it.
//   <button data-action="clipboard#copy">
//     <span class="inline-flex items-center gap-2" data-clipboard-target="idle">Copy link</span>
//     <span class="hidden items-center gap-2" data-clipboard-target="copied">Copied</span>
//   </button>
//
// Icon swap — a square button carrying no text at all, where the copy glyph
// trades for a tick. Only `hidden` moves here: neither span owns a display
// utility, so there is nothing for the toggle to fight over.
//   <button data-action="clipboard#copy">
//     <span data-clipboard-target="icon"><%= inline_svg_tag 'svg/copy.svg' %></span>
//     <span data-clipboard-target="check" class="hidden"><%= inline_svg_tag 'svg/check.svg' %></span>
//   </button>
//
// Exactly one label carries a display class at a time — toggling `hidden` alone
// would leave two display utilities on the span and let source order decide.
export default class extends Controller {
  static targets = ["label", "idle", "copied", "icon", "check"];
  static values = { text: String, revertAfter: { type: Number, default: 1600 } };

  declare readonly hasLabelTarget: boolean;
  declare readonly labelTarget: HTMLElement;
  declare readonly hasIdleTarget: boolean;
  declare readonly idleTarget: HTMLElement;
  declare readonly hasCopiedTarget: boolean;
  declare readonly copiedTarget: HTMLElement;
  declare readonly hasIconTarget: boolean;
  declare readonly iconTarget: HTMLElement;
  declare readonly hasCheckTarget: boolean;
  declare readonly checkTarget: HTMLElement;
  declare readonly textValue: string;
  declare revertAfterValue: number;
  declare timeout: ReturnType<typeof setTimeout> | undefined;

  // The sheet this runs in re-renders on publish and revoke, so a pending
  // revert would otherwise fire against a detached label.
  disconnect() {
    clearTimeout(this.timeout);
  }

  async copy(event: Event) {
    event.preventDefault();

    try {
      await navigator.clipboard.writeText(this.textValue);
    } catch {
      return;
    }

    // A second click during the revert window restarts the confirmation rather
    // than letting the first one's timer cut the second one short.
    clearTimeout(this.timeout);

    if (this.hasIdleTarget && this.hasCopiedTarget) {
      this.showOnly(this.copiedTarget);
      this.timeout = setTimeout(() => this.showOnly(this.idleTarget), this.revertAfterValue);
      return;
    }

    if (this.hasIconTarget && this.hasCheckTarget) {
      this.iconTarget.classList.add("hidden");
      this.checkTarget.classList.remove("hidden");
      this.timeout = setTimeout(() => {
        this.iconTarget.classList.remove("hidden");
        this.checkTarget.classList.add("hidden");
      }, this.revertAfterValue);
      return;
    }

    if (!this.hasLabelTarget) return;

    const label = this.labelTarget;
    // Stashed rather than re-read: a second click lands while the label still
    // says "Copied", and reading it then would make that the value we restore.
    const original = label.dataset.idle || this.read(label);
    label.dataset.idle = original;
    this.write(label, label.dataset.copied || "Copied");

    this.timeout = setTimeout(() => {
      this.write(label, original);
    }, this.revertAfterValue);
  }

  private showOnly(active: HTMLElement) {
    for (const label of [this.idleTarget, this.copiedTarget]) {
      const visible = label === active;
      label.classList.toggle("hidden", !visible);
      label.classList.toggle("inline-flex", visible);
    }
  }

  // An icon-only button has no text of its own, so its tooltip is the label.
  private read(label: HTMLElement): string {
    return label.dataset.tip ?? label.textContent ?? "";
  }

  private write(label: HTMLElement, value: string) {
    if (label.dataset.tip !== undefined) {
      label.dataset.tip = value;
    } else {
      label.textContent = value;
    }
  }
}
